import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { Button } from "../Buttons/Button";
import PropTypes from "prop-types";
import { Formik } from "formik";
import FormInput from "./FormInput";
import { InfoCircleFill } from "react-bootstrap-icons";
import classNames from "classnames";
import { useLocation } from "react-router";
import Spinner from "react-bootstrap/Spinner";
import { useAuth } from "../../components/AuthContext/AuthContext";

/**
 * SearchForm component for rendering a search form with dynamic fields.
 *
 * @param {Object} props - The props object.
 * @param {Object} props.fields - The fields configuration object.
 * @param {number} props.colStructure - The column structure for form layout.
 */

function SearchForm(props) {
  const [showMessage, setShowMessage] = useState(false);
  const [formErrorState, setFormErrorState] = useState("");
  const [errorKey, setErrorKey] = useState(0);

  const {
    children,
    className,
    colStructure,
    description,
    fieldError,
    fields,
    helptext,
    helptextClass,
    isEditMode,
    onBackClick,
    onBlur,
    onCancelClick,
    onEditClick,
    onRejectClick,
    onSubmit,
    useLocalStorage,
    serverError,
    data,
    validationSchema,
    storeName,
    onKeyDown,
    onSelectChange,
    values: propsValues,
    textAfter,
    isRejectDisabled,
    isLoading,
    subtitle = "Enter participant details to continue",
    title = "Participant record",
    childrenNotes,
    isVhlogin = false,
    onFormError,
    hideFormError,
  } = props;

  // Handle error message display
  let selectedService = localStorage.getItem("selectedService");
  const isPatientCard = localStorage.getItem("patientCard") === "true";
  const location = useLocation();
  const auth = useAuth();
  useEffect(() => {
    if (showMessage && props.formError) {
      setFormErrorState(props.formError);
      onFormError?.(props.formError);
      const timer = setTimeout(() => {
        setShowMessage(false);
        setFormErrorState("");
        onFormError?.("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showMessage, props.formError, errorKey]);

  useEffect(() => {
    return () => {
      onFormError?.("");
    };
  }, []);

  // Generate initial form values
  const generateInitialValues = () => {
    const initialValues = {};

    fields.forEach((field) => {
      if (field.type === "button" || field.type === "submit") {
        return;
      }

      if (field.type === "dobGroup") {
        field.fields.forEach((sub) => {
          initialValues[sub.name] =
            data?.[sub.name] === undefined
              ? sub.value || ""
              : data[sub.name];
        });
        return;
      }

      if (field.type === "dateGroup") {
        const storedDate =
          null ||
          data?.[field.name] ||
          "";
        let day = "",
          month = "",
          year = "";
        if (storedDate && storedDate.includes("-")) {
          const parts = storedDate.split("-");
          if (parts.length === 3) {
            year = parts[0];
            month = parts[1];
            day = parts[2];
          }
        }
        initialValues[`${field.name}_day`] = data?.[`${field.name}_day`] || day;
        initialValues[`${field.name}_month`] =
          data?.[`${field.name}_month`] || month;
        initialValues[`${field.name}_year`] =
          data?.[`${field.name}_year`] || year;
        initialValues[field.name] =
          storedDate ||
          (day && month && year
            ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
            : "");
        return;
      }

      if (field.type === "timeGroup") {
        const storedTime =
          null ||
          data?.[field.name] ||
          "";
        let hour = "",
          minute = "";
        if (storedTime && storedTime.includes(":")) {
          const parts = storedTime.split(":");
          hour = parts[0];
          minute = parts[1];
        }
        initialValues[`${field.name}_hour`] =
          data?.[`${field.name}_hour`] || hour;
        initialValues[`${field.name}_minute`] =
          data?.[`${field.name}_minute`] || minute;
        initialValues[field.name] =
          storedTime ||
          (hour && minute
            ? `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
            : "");
        return;
      }

      if (field.type === "radioGroup") {
        const storedRadio =
          null ||
          data?.[field.name] ||
          field.value ||
          "";
        initialValues[field.name] = storedRadio;
        return;
      }

      initialValues[field.name] =
        data?.[field.name] === undefined ? field.value || "" : data[field.name];
    });

    return initialValues;
  };

  // Extract form submission logic
const handleFormSubmit = (values) => {
  delete values?.undefined;

  const dobGroupField = fields.find((f) => f.type === "dobGroup");
  if (dobGroupField) {
    const { dob_day, dob_month, dob_year } = values;
    if (dob_day && dob_month && dob_year) {
      const dd = String(dob_day).padStart(2, "0");
      const mm = String(dob_month).padStart(2, "0");
      values[dobGroupField.name] = `${dob_year}-${mm}-${dd}`;
    } else {
      values[dobGroupField.name] = "";
    }
  }

  const dateGroupField = fields.find((f) => f.type === "dateGroup");
  if (dateGroupField) {
    const d = values[`${dateGroupField.name}_day`];
    const m = values[`${dateGroupField.name}_month`];
    const y = values[`${dateGroupField.name}_year`];
    if (d && m && y) {
      values[dateGroupField.name] = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    } else {
      values[dateGroupField.name] = "";
    }
  }

  const timeGroupField = fields.find((f) => f.type === "timeGroup");
  if (timeGroupField) {
    const h = values[`${timeGroupField.name}_hour`];
    const min = values[`${timeGroupField.name}_minute`];
    if (h !== undefined && h !== "" && min !== undefined && min !== "") {
      values[timeGroupField.name] = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    }
  }

  if (fieldError) {
    const filledCount = fields.filter((field) => values[field.name]).length;
    const minRequired = selectedService === "clinical" ? 6 : 3;

    if (filledCount >= minRequired) {
      onSubmit?.(values);
    } else {
      setShowMessage(true);
      setErrorKey((prev) => prev + 1);
    }
  } else {
    onSubmit?.(values);
  }
};

  // Render a regular form input
  const renderFormInput = ({
    field,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    touched,
    errors,
  }) => (
    <FormInput
      as={field.as}
      className={`form-group ${field.className} ${
        field.type === "date" && field.value ? "placeholder-text" : ""
      }`}
      error={errors[field.name]}
      helpText={
        field.name === "primary_barcode" ? helptext || "" : field.helpText || ""
      }
      helptextClass={helptextClass}
      helpTextAbove={field.helpTextAbove}
      image={field.FormFieldImage}
      key={field.name}
      label={field.label}
      maxLength={field.maxLength}
      name={field.name}
      lottNumber={props.lottNumber}
      onBlur={(e) => {
        handleBlur(e);
        if (field.name === "primary_barcode") {
          onBlur?.(e);
        }
      }}
      onChange={(e) => {
        handleChange(e);
        if (field.type === "select") {
          setFieldValue(field.name, e.target.value);
        }
      }}
   onKeyDown={(e) => {
      if (
        ["dob_day", "dob_month", "dob_year"].includes(field.name) &&
        !/[0-9]/.test(e.key) &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
          e.key
        )
      ) {
        e.preventDefault();
      }

      if (field.name === "primary_barcode" && e.key === "Enter") {
        e.preventDefault();
        handleFormSubmit();
      }

      if (field.name === "primary_barcode") {
        onKeyDown?.(e);
      }
    }}
      onSelectChange={onSelectChange}
      options={field.options || []}
      placeholder={field.placeholder}
      readOnly={!isEditMode && field.readOnly}
      required={field.required}
      storeName={storeName}
      touched={touched[field.name]}
      type={field.type}
      useLocalStorage={useLocalStorage}
      value={
        field.type === "text-area"
          ? propsValues
          : field.value || values[field.name]
      }
      autoComplete="off"
      isdisabled={field.isdisabled}
       inputMode={
      ["dob_day", "dob_month", "dob_year"].includes(field.name)
        ? "numeric"
        : undefined
    }
    />
  );

  // Render edit mode buttons
  const renderEditModeButtons = (handleSubmit) => (
    <>
      <Col md={6}>
        <Button
          className="buttons float-right"
          onClick={onCancelClick}
          variant="danger"
        >
          Cancel
        </Button>
      </Col>
      <Col md={6}>
        <Button
          className="buttons float-right"
          onClick={handleSubmit}
          variant="secondary"
        >
          Save
        </Button>
      </Col>
    </>
  );

  // Render button group
  const renderButtonGroup = (field) => (
    <div className="confirm-details-buttons">
      {field.btnGroup.map((button) => (
        <Button
          key={button.label}
          className={classNames("buttons float-right", button.className)}
          disabled={button.label === "Reject" ? isRejectDisabled : false}
          helpTextAbove={props.helpTextAbove}
          iconOrder={button.iconOrder}
          label={button.label}
          onClick={() => {
            if (button.name === "back" || button.label === "Cancel") {
              return onBackClick();
            } else if (button.label === "EDIT") {
              return onEditClick();
            } else if (button.label === "Reject" && onRejectClick) {
              return onRejectClick();
            }
          }}
          size={button.size}
          svg={button.icon}
          type={button.type}
          variant={button.variant}
        >
          {button.label}
        </Button>
      ))}
    </div>
  );

  // Render single button
  const renderSingleButton = (field) => (
    <Button
      className={classNames("buttons float-right ", field.className)}
      disabled={field.label === "REJECT" ? isRejectDisabled : false}
      helpTextAbove={props.helpTextAbove}
      iconOrder={field.iconOrder}
      label={field.label}
      onClick={() => {
        if (field.name === "back") {
          return onBackClick();
        } else if (field.label === "EDIT") {
          return onEditClick();
        } else if (field.label === "REJECT") {
          return onRejectClick();
        } else {
          return null;
        }
      }}
      size={field.size}
      svg={field.icon}
      type={field.type}
      variant={field.variant}
      serverError={serverError}
    >
      {field.label}
    </Button>
  );

  // Determine column class based on field type
  const getColumnClass = (field) => {
    if (field.type === "button" || field.type === "submit") return "col-md-12";
    if (field.type === "date") return "birth-date-field";
    if (field.type === "time") return "time-field";
    if (field.type === "select") return "attempts-field";
    return "";
  };

  // Determine column size
  const getColumnSize = (field) => {
    if (field.label === "Search" && field.type === "submit") {
      return 12;
    }
    if (field.btnGroup) {
      return field.name === "Notes" ? 6 : undefined;
    }
    return field.colSize || colStructure;
  };

  const renderFieldContent = ({
    field,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    touched,
    errors,
    isEditMode,
    handleSubmit,
  }) => {
      if (field.type === "dobGroup") {
    return (
      <div className="dob-group-wrapper">
        <label className="form-label">{field.label}</label>

        <Row>
          {field.fields.map((item) => (
            <Col key={item.name} md={item.colSize || 4}>
              {renderFormInput({
                field: item,
                handleChange,
                handleBlur,
                setFieldValue,
                values,
                touched,
                errors
        })}
            </Col>
          ))}
        </Row>
      </div>
    );
  }

    if (field.type === "radioGroup") {
      const isError =
        (touched[field.name] || touched?._submitCount) && !!errors[field.name];
      return (
        <div className="vh-attempts-group">
          {field.label && (
            <Form.Label className="labels">{field.label}</Form.Label>
          )}
          <div className="vh-radio-options">
            {field.options?.map((option) => {
              const optVal =
                typeof option === "object" ? option.value : option;
              const optLabel =
                typeof option === "object" ? option.label : option;
              const isChecked = String(values[field.name]) === String(optVal);
              return (
                <Form.Check
                  key={optVal}
                  type="radio"
                  id={`${field.name}-${optVal}`}
                  name={field.name}
                  label={optLabel}
                  value={optVal}
                  checked={isChecked}
                  onChange={() => {
                    setFieldValue(field.name, optVal);

                  }}
                  onClick={() => {
                    setFieldValue(field.name, optVal);

                  }}
                  isInvalid={isError}
                  className="vh-radio-option"
                />
              );
            })}
          </div>
          {isError && (
            <div className="invalid-feedback d-flex align-items-center gap-1 mt-1 text-danger show">
              <InfoCircleFill /> {errors[field.name]}
            </div>
          )}
        </div>
      );
    }

    if (field.type === "dateGroup") {
      const isError =
        (touched[field.name] ||
          touched[`${field.name}_day`] ||
          touched[`${field.name}_month`] ||
          touched[`${field.name}_year`]) &&
        !!errors[field.name];
      const dayVal = values[`${field.name}_day`] || "";
      const monthVal = values[`${field.name}_month`] || "";
      const yearVal = values[`${field.name}_year`] || "";

      const onDateSubfieldChange = (part, val) => {
        setFieldValue(`${field.name}_${part}`, val);
        const d = part === "day" ? val : values[`${field.name}_day`];
        const m = part === "month" ? val : values[`${field.name}_month`];
        const y = part === "year" ? val : values[`${field.name}_year`];
        if (d && m && y && String(y).length === 4) {
          const comp = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          setFieldValue(field.name, comp);

        } else {
          setFieldValue(field.name, "");
        }
      };

      return (
        <div className="vh-date-group">
          {field.label && (
            <Form.Label className="labels">{field.label}</Form.Label>
          )}
          <div className="vh-inputs-row">
            <div className="vh-subfield vh-subfield--day">
              <span className="vh-sublabel">Day</span>
              <Form.Control
                type="text"
                name={`${field.name}_day`}
                maxLength={2}
                value={dayVal}
                placeholder=""
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  onDateSubfieldChange("day", val);
                }}
                onBlur={handleBlur}
                isInvalid={isError}
                className="vh-date-input"
              />
            </div>
            <div className="vh-subfield vh-subfield--month">
              <span className="vh-sublabel">Month</span>
              <Form.Control
                type="text"
                name={`${field.name}_month`}
                maxLength={2}
                value={monthVal}
                placeholder=""
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  onDateSubfieldChange("month", val);
                }}
                onBlur={handleBlur}
                isInvalid={isError}
                className="vh-date-input"
              />
            </div>
            <div className="vh-subfield vh-subfield--year">
              <span className="vh-sublabel">Year</span>
              <Form.Control
                type="text"
                name={`${field.name}_year`}
                maxLength={4}
                value={yearVal}
                placeholder=""
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  onDateSubfieldChange("year", val);
                }}
                onBlur={handleBlur}
                isInvalid={isError}
                className="vh-date-input vh-year-input"
              />
            </div>
          </div>
          {isError && (
            <div className="invalid-feedback d-flex align-items-center gap-1 mt-1 text-danger show">
              <InfoCircleFill /> {errors[field.name]}
            </div>
          )}
        </div>
      );
    }

    if (field.type === "timeGroup") {
      const isError =
        (touched[field.name] ||
          touched[`${field.name}_hour`] ||
          touched[`${field.name}_minute`]) &&
        !!errors[field.name];
      const hourVal = values[`${field.name}_hour`] || "";
      const minuteVal = values[`${field.name}_minute`] || "";

      const onTimeSubfieldChange = (part, val) => {
        setFieldValue(`${field.name}_${part}`, val);
        const h = part === "hour" ? val : values[`${field.name}_hour`];
        const min = part === "minute" ? val : values[`${field.name}_minute`];
        if (h !== undefined && h !== "" && min !== undefined && min !== "") {
          const comp = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
          setFieldValue(field.name, comp);
        } else {
          setFieldValue(field.name, "");
        }
      };

      return (
        <div className="vh-time-group">
          {field.label && (
            <Form.Label className="labels">{field.label}</Form.Label>
          )}
          <div className="vh-inputs-row">
            <div className="vh-subfield vh-subfield--hours">
              <span className="vh-sublabel">Hours</span>
              <Form.Control
                type="text"
                name={`${field.name}_hour`}
                maxLength={2}
                value={hourVal}
                placeholder="00"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  onTimeSubfieldChange("hour", val);
                }}
                onBlur={handleBlur}
                isInvalid={isError}
                className="vh-time-input"
              />
            </div>
            <span className="vh-time-separator">:</span>
            <div className="vh-subfield vh-subfield--minutes">
              <span className="vh-sublabel">Minutes</span>
              <Form.Control
                type="text"
                name={`${field.name}_minute`}
                maxLength={2}
                value={minuteVal}
                placeholder="00"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  onTimeSubfieldChange("minute", val);
                }}
                onBlur={handleBlur}
                isInvalid={isError}
                className="vh-time-input"
              />
            </div>
          </div>
          {isError && (
            <div className="invalid-feedback d-flex align-items-center gap-1 mt-1 text-danger show">
              <InfoCircleFill /> {errors[field.name]}
            </div>
          )}
        </div>
      );
    }
    if (
      field.type !== "button" &&
      field.type !== "submit" &&
      field.type !== "barcode-text" &&
      !field.btnGroup
    ) {
      return renderFormInput({
        field,
        handleChange,
        handleBlur,
        setFieldValue,
        values,
        touched,
        errors,
      });
    }

    if (isEditMode && field.type !== "barcode-text") {
      return renderEditModeButtons(handleSubmit);
    }

    if (field.btnGroup?.length && field.type !== "barcode-text") {
      return renderButtonGroup(field);
    }

    if (field.type !== "barcode-text") {
      return renderSingleButton(field);
    }

    return null; // Default case if none of the conditions are met
  };

  const initialValues = generateInitialValues();

  return (
    <div
      className={classNames(
        `patient-record-form ${isPatientCard && "thrive-wrapper"}`,
        className,
      )}
    >
      {/* Display error message if needed */}
      {!hideFormError && showMessage && formErrorState && (
        <span className="server-error">
          <InfoCircleFill />
          {formErrorState}
        </span>
      )}

      {/* Render description if provided */}
      {description && selectedService !== "clinical" && <h2>{description}</h2>}
      {!auth?.featureFlag && isPatientCard && selectedService === "clinical" ? (
        <>
          <h3>Thrive Registry</h3>
          <p>
            Test : <span>Thrive Panel</span>
          </p>
        </>
      ) : (
        ""
      )}
      <div
        className={`${
          isPatientCard && selectedService === "clinical" || isVhlogin
            ? "patient-record-card"
            : ""
        }`}
      >
        {(!auth?.featureFlag && children && selectedService === "clinical") ? (
          <div className={`${isPatientCard && "card-header"}`}>
            <h3>Appointment confirmation</h3>
            <p>Please confirm details to continue registration</p>
          </div>
        ) : (
          <div className={`${!auth?.featureFlag && isPatientCard && "card-header"}`}>
            {title !== "null" && <h3 className="patient-record-title ageo-bold">{title}</h3>}
            {subtitle !== "null" && <p>{subtitle}</p>}
          </div>
        )}
        {!auth?.featureFlag && isPatientCard && selectedService === "clinical" && (
          <div className="patient-card-seperator"></div>
        )}
        {children ? (
          <>
            {/* <h2 className="test-registration-description px-4">
              The following test will be registered
            </h2> */}
            {isLoading && (
              <div className="text-center">
                <Spinner />
              </div>
            )}{" "}
            {children}
          </>
        ) : (
          ""
        )}
        <Formik
          innerRef={props.formikRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleFormSubmit}
          validate={(values) => {
            const dateGroupField = fields.find((f) => f.type === "dateGroup");
            if (dateGroupField) {
              const d = values[`${dateGroupField.name}_day`];
              const m = values[`${dateGroupField.name}_month`];
              const y = values[`${dateGroupField.name}_year`];
              if (d && m && y && String(y).length === 4) {
                values[dateGroupField.name] = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              }
            }
            const timeGroupField = fields.find((f) => f.type === "timeGroup");
            if (timeGroupField) {
              const h = values[`${timeGroupField.name}_hour`];
              const min = values[`${timeGroupField.name}_minute`];
              if (h !== undefined && h !== "" && min !== undefined && min !== "") {
                values[timeGroupField.name] = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
              }
            }
          }}
          validateOnBlur={false}
          validateOnChange={props.validateOnChange || false}
          validationSchema={validationSchema}
        >
          {({
            errors,
            handleSubmit,
            handleChange,
            handleBlur,
            setFieldValue,
            touched,
            values,
          }) => (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className={`${props.formClass} ${location?.pathname !== "/search" && "container-fluid px-0"}`}
            >
              <div
                className={`${isPatientCard ? "" : "patient-record-card"} ${ location?.pathname === "/patient-record" && "health-care-card"}`}
              >
                <Row>
                  {selectedService !== "healthCare" && (
                    <div className="col-md-12">{props.nametag}</div>
                  )}
                  {fields.map((field) => (
                    <React.Fragment key={field.name || field.label}>
                      {field.type === "barcode-text" && (
                        <div className="col-md-12 before-wrapper">
                          <span className="serial-no">1. </span>
                          <span className="underline-text">
                            <span
                              className={`${isPatientCard ? "" : "danger-text"}`}
                            >
                              Before
                            </span>{" "}
                            drawing blood,
                          </span>{" "}
                          enter the kit barcode number
                        </div>
                      )}

                      <Col
                        className={` ${getColumnClass(field)} ${
                          location.pathname === "/patient-record/select-patient"
                            ? "px-4"
                            : ""
                        } ${location.pathname.includes("/enter-barcode/") ? "mb-0" : "mb-3"} ${
                          ["primary_barcode", "secondary_barcode"].includes(field.name)
                            ? "vh-barcode-col"
                            : ""
                        }`}
                        md={getColumnSize(field)}
                      >
                        <Form.Group>
                          <div
                            className={
                              field.name === "secondary_barcode"
                                ? "barcode-group secondary-barcode-group"
                                : ""
                            }
                          >
                            {renderFieldContent({
                              field,
                              handleChange,
                              handleBlur,
                              setFieldValue,
                              values,
                              touched,
                              errors,
                              isEditMode,
                              handleSubmit,
                            })}
                          </div>
                        </Form.Group>
                      </Col>

                      {isVhlogin && field.name === "test_type" && (
                        <Col md={12}>
                          <hr className="vh-divider" />
                        </Col>
                      )}

                      {(field.hrAfter || field.textAfter) && (
                        <Col md={12}>
                          {selectedService === "Healthcare" && field.hrAfter && (
                            <hr className="mt-0 mb-4" />
                          )}
                          {field.textAfter && (
                            <h2 className="date-time-attempts-title help-text-above">
                              {textAfter}
                            </h2>
                          )}
                        </Col>
                      )}
                    </React.Fragment>
                  ))}
                  <Col md={12}>{props.child}</Col>
                </Row>
              </div>
              {childrenNotes && (
                <div className="notes-wrapper">
                  {childrenNotes}
                </div>
              )}
            </Form>
          )}
        </Formik>
        {/* <div className="table-responsive">
          <Table
            data={props?.modifiedBookings}
            pagination={true}
            search={true}
            sort={true}
          />
        </div> */}
      </div>
    </div>
  );
}

SearchForm.propTypes = {
  validationSchema: PropTypes.objectOf(PropTypes.any),
  lottNumber: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      controlId: PropTypes.number,
      label: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      placeholder: PropTypes.string,
      readOnly: PropTypes.bool,
      required: PropTypes.bool,
      variant: PropTypes.string,
      className: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        }),
      ),
      helpText: PropTypes.string,
      helpTextAbove: PropTypes.string,
      FormFieldImage: PropTypes.node,
      colSize: PropTypes.number,
      hrAfter: PropTypes.bool,
      textAfter: PropTypes.string,
    }),
  ),
  textAfter: PropTypes.string,
  colStructure: PropTypes.number,
  formError: PropTypes.string,
  formClass: PropTypes.string,
  onSubmit: PropTypes.func,
  description: PropTypes.string,
  data: PropTypes.object,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  onBackClick: PropTypes.func,
  helptext: PropTypes.string,
  isEditMode: PropTypes.bool,
  onEditClick: PropTypes.func,
  onCancelClick: PropTypes.func,
  onRejectClick: PropTypes.func,
  storeName: PropTypes.string,
  onSelectChange: PropTypes.string,
  isRejectDisabled: PropTypes.bool,
  values: PropTypes.string,
  useLocalStorage: PropTypes.bool,
  fieldError: PropTypes.bool,
  serverError: PropTypes.string,
  onKeyDown: PropTypes.func,
  helpTextAbove: PropTypes.string,
  helptextClass: PropTypes.string,
  validateOnChange: PropTypes.bool,
  children: PropTypes.node,
  isLoading: PropTypes.bool,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  formikRef: PropTypes.object,
  child: PropTypes.node,
  nametag: PropTypes.string,
  onFormError: PropTypes.func,
  hideFormError: PropTypes.bool,
};

export default SearchForm;
